// ============================================================
// AuthModal
// ============================================================
// Sign in / sign up modal with email + password.
// ============================================================

import React, { useState, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';

interface AuthModalProps {
  onClose: () => void;
}

export function AuthModal({ onClose }: AuthModalProps) {
  const signIn   = useAuthStore((s) => s.signIn);
  const signUp   = useAuthStore((s) => s.signUp);
  const loading  = useAuthStore((s) => s.loading);

  const [mode, setMode]       = useState<'signin' | 'signup'>('signin');
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    setError(null);
    setSuccess(null);

    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password.');
      return;
    }

    if (mode === 'signup') {
      const result = await signUp(email, password);
      if (!result.ok) {
        setError(result.error ?? 'Sign up failed.');
      } else {
        setSuccess('Account created! Check your email to confirm, then sign in.');
        setMode('signin');
      }
    } else {
      const result = await signIn(email, password);
      if (!result.ok) {
        setError(result.error ?? 'Sign in failed.');
      } else {
        onClose();
      }
    }
  }, [mode, email, password, signIn, signUp, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 600,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 700,
          width: 360,
          background: 'var(--color-paper)',
          border: '1px solid var(--color-rule)',
          borderRadius: 10,
          boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
          padding: '32px 28px',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 22,
            color: 'var(--color-ink-primary)',
            marginBottom: 4,
          }}>
            {mode === 'signin' ? 'Sign in' : 'Create account'}
          </h2>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-ink-muted)' }}>
            {mode === 'signin'
              ? 'Sign in to sync and share your charts.'
              : 'Create an account to save and share charts.'}
          </p>
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              padding: '9px 12px',
              background: 'var(--color-paper-alt)',
              border: '1px solid var(--color-rule)',
              borderRadius: 6,
              outline: 'none',
              color: 'var(--color-ink-primary)',
            }}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              padding: '9px 12px',
              background: 'var(--color-paper-alt)',
              border: '1px solid var(--color-rule)',
              borderRadius: 6,
              outline: 'none',
              color: 'var(--color-ink-primary)',
            }}
          />
        </div>

        {/* Error / success */}
        {error && (
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            color: 'var(--color-error)',
            marginBottom: 12, padding: '6px 10px',
            background: 'var(--color-error-bg)',
            borderRadius: 4,
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: 11,
            color: 'var(--color-ink-secondary)',
            marginBottom: 12, padding: '6px 10px',
            background: 'var(--color-hover)',
            borderRadius: 4,
          }}>
            {success}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            width: '100%',
            padding: '10px',
            background: 'var(--color-accent)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
            marginBottom: 12,
          }}
        >
          {loading ? '…' : mode === 'signin' ? 'Sign in' : 'Create account'}
        </button>

        {/* Toggle mode */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => { setMode(mode === 'signin' ? 'signup' : 'signin'); setError(null); setSuccess(null); }}
            style={{
              fontFamily: 'var(--font-mono)', fontSize: 11,
              color: 'var(--color-ink-muted)',
              background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            {mode === 'signin' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    </>
  );
}
